import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, ArrowRight, ArrowDown, Activity, Brain, Shield, Zap, FlaskConical, LineChart, BookOpen, Users, Menu, X } from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <div className="bg-hikari min-h-screen">
      {/* Background orbs */}
      <div className="orb orb-mint"></div>
      <div className="orb orb-violet"></div>
      <div className="orb orb-blue"></div>

      <div className="content-wrapper">
        {/* Header */}
        <header className="sticky top-0 z-50 glass mx-4 md:mx-8 mt-4 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2DD4BF] to-[#2DD4BF] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#0A0B10]" />
            </div>
            <div>
              <div className="kicker text-[#2DD4BF]">Hikari Health Systems</div>
              <div className="text-lg font-semibold">Body Compass</div>
            </div>
          </div>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm text-[#CBD5E1]">
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            {user ? (
              <Link to="/tracker" className="btn-primary text-sm py-2 px-4" data-testid="header-tracker-link">
                Open Tracker
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[#CBD5E1] hover:text-white transition-colors" data-testid="header-login-link">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm py-2.5 px-5" data-testid="header-register-link">
                  Start Free
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-[#CBD5E1]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden glass mx-4 mt-2 p-4 space-y-4">
            <a href="#how-it-works" className="block text-[#CBD5E1] hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>How it works</a>
            <a href="#features" className="block text-[#CBD5E1] hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Features</a>
            {user ? (
              <Link to="/tracker" className="btn-primary w-full text-center block py-2" onClick={() => setMobileMenuOpen(false)}>
                Open Tracker
              </Link>
            ) : (
              <>
                <Link to="/login" className="block text-[#CBD5E1] hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary w-full text-center block py-2.5" onClick={() => setMobileMenuOpen(false)}>
                  Start Free
                </Link>
              </>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* HERO SECTION - Above the fold */}
        {/* ============================================ */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-16 text-center">
          <div className="opacity-0 animate-fade-in-up">
            {/* Beta Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 text-[#2DD4BF] text-sm mb-8">
              <Users className="w-4 h-4" />
              Currently in private beta
            </div>
            
            {/* Main Headline - H1 dominant */}
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] mb-6 tracking-tight">
              Your body is speaking.<br />
              <span className="text-[#2DD4BF]">Body Compass</span> helps you listen.
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-[#CBD5E1] leading-relaxed mb-10 max-w-2xl mx-auto">
              Track symptoms, map biological pathways, and get personalised lab suggestions — all privately, on your device.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              {user ? (
                <Link to="/tracker" className="btn-primary text-lg py-4 px-8 flex items-center gap-2" data-testid="hero-tracker-button">
                  Open Tracker <ArrowRight className="w-5 h-5" />
                </Link>
              ) : (
                <Link to="/register" className="btn-primary text-lg py-4 px-8 flex items-center gap-2" data-testid="hero-get-started-button">
                  Start Free <ArrowRight className="w-5 h-5" />
                </Link>
              )}
              <a href="#how-it-works" className="btn-secondary text-lg py-4 px-8 flex items-center gap-2" data-testid="hero-learn-more-button">
                See how it works <ArrowDown className="w-5 h-5" />
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#CBD5E1]">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#2DD4BF]" />
                <span>100% local storage</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#2DD4BF]" />
                <span>No account required to track</span>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#2DD4BF]" />
                <span>Functional medicine approach</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* HOW IT WORKS - System Diagram */}
        {/* ============================================ */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16" id="how-it-works">
          <div className="text-center mb-12">
            <div className="kicker text-[#2DD4BF] mb-4">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-light mb-4">
              From felt experience to structured clarity
            </h2>
            <p className="text-[#CBD5E1] max-w-2xl mx-auto">
              Each layer has one job: capture signals, translate them into systems, suggest the next look, 
              and keep you in control of your own record.
            </p>
          </div>

          <div className="glass p-6 md:p-8">
            <div className="grid md:grid-cols-5 gap-4 mb-6">
              {[
                { num: '01', title: 'Symptom Input', desc: 'Energy, sleep, pain, digestion, mood, cycle.' },
                { num: '02', title: 'System Mapping', desc: 'Translate symptoms into biological pathways.' },
                { num: '03', title: 'Test Suggestions', desc: 'Commonly used labs to explore, not diagnose.' },
                { num: '04', title: 'Verified Labs', desc: 'QR-linked orders, identity shielding, audit.' },
                { num: '05', title: 'Interpretation', desc: 'Readable markers, trends, next questions.' }
              ].map((step, i) => (
                <div key={i} className="glass-subtle p-5 text-center relative">
                  {/* Unified teal color for all step numbers */}
                  <span className="kicker text-[#2DD4BF] mb-2 block">{step.num}</span>
                  <h3 className="font-semibold mb-2 text-white">{step.title}</h3>
                  {/* Improved contrast for body text */}
                  <p className="text-sm text-[#CBD5E1]">{step.desc}</p>
                  {i < 4 && (
                    <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
                      <ArrowRight className="w-5 h-5 text-[#64748B]" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="glass-subtle p-5 text-center">
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Shield className="w-5 h-5 text-[#2DD4BF]" />
                <span className="text-sm text-[#CBD5E1]">
                  <strong className="text-white">Trust architecture:</strong> Local-only storage • Export by choice • 
                  Private snapshots • User-owned record
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* FEATURES SECTION */}
        {/* ============================================ */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-16" id="features">
          <div className="text-center mb-12">
            <div className="kicker text-[#2DD4BF] mb-4">Features</div>
            <h2 className="font-display text-3xl sm:text-4xl font-light mb-4">
              Everything you need to understand your body
            </h2>
            <p className="text-[#CBD5E1] max-w-2xl mx-auto">
              A functioning symptom engine with local saving, pathway weighting, and AI-powered learning.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: Activity,
                title: 'Daily Signal Entry',
                desc: 'Severity sliders, symptom chips, daily notes, supplements — all saved locally with no backend required.',
                kicker: 'Track'
              },
              {
                icon: Brain,
                title: 'Body-System Mapping',
                desc: 'A transparent rules engine weights thyroid, nutrient, inflammatory, and other pathways based on your input.',
                kicker: 'Map'
              },
              {
                icon: FlaskConical,
                title: 'Lab Pathway Suggestions',
                desc: 'Translates top pathways into exploratory labs and shows why each panel may be relevant.',
                kicker: 'Explore'
              }
            ].map((feature, i) => (
              <div key={i} className="glass-card p-8 opacity-0 animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="kicker text-[#2DD4BF] mb-4">{feature.kicker}</div>
                <div className="w-12 h-12 rounded-xl bg-[#2DD4BF]/15 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#2DD4BF]" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-[#CBD5E1] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Additional features row */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-8">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-[#2DD4BF]/15 flex items-center justify-center flex-shrink-0">
                  <LineChart className="w-6 h-6 text-[#2DD4BF]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white">7-Day Trend Charts</h3>
                  <p className="text-[#CBD5E1] leading-relaxed">
                    Visualize your energy, sleep, and inflammation patterns over time. 
                    Spot correlations and track progress with beautiful, interactive charts.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-[#2DD4BF]/15 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-[#2DD4BF]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white">AI Tutor</h3>
                  <p className="text-[#CBD5E1] leading-relaxed">
                    Learn functional medicine with Hikari AI — a 28-lesson curriculum 
                    covering gut health, hormones, inflammation, lab interpretation, and more.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* SOCIAL PROOF / TESTIMONIAL */}
        {/* ============================================ */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-16">
          <div className="glass p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center flex-shrink-0 text-2xl font-display text-[#0A0B10]">
                S
              </div>
              <div>
                <blockquote className="text-lg md:text-xl text-white leading-relaxed mb-4">
                  "I've tried dozens of symptom trackers. Body Compass is the first one that actually helps me see <em>why</em> I'm feeling a certain way, not just <em>that</em> I'm feeling it. The pathway mapping changed how I talk to my functional medicine practitioner."
                </blockquote>
                <div className="text-[#CBD5E1]">
                  <strong className="text-white">Sarah M.</strong> — Beta user, chronic fatigue recovery
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* FINAL CTA */}
        {/* ============================================ */}
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-16">
          <div className="glass p-8 md:p-12 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-light mb-4 text-white">
              Ready to understand your body better?
            </h2>
            <p className="text-[#CBD5E1] mb-8 max-w-xl mx-auto">
              Start tracking your symptoms, discover patterns, and learn functional medicine —
              all while keeping your data completely private.
            </p>
            {user ? (
              <Link to="/tracker" className="btn-primary inline-flex items-center gap-2 text-lg py-4 px-8" data-testid="cta-tracker-button">
                Open Tracker <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-lg py-4 px-8" data-testid="cta-register-button">
                Create Free Account <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="max-w-7xl mx-auto px-4 md:px-8 py-8 text-center text-[#64748B] text-sm">
          <p>Hikari Body Compass — Privacy-first functional medicine tracking</p>
        </footer>
      </div>
    </div>
  );
}
